// Shared 4-state publication logic (draft / coming / available / past).
// Mirrors the backend engine in server.py so Admin preview and public site agree.
const RANK = { draft: 0, coming: 1, available: 2, past: 3 };

export const baseState = (it = {}) => {
  if (["draft", "coming", "available", "past"].includes(it.publishStatus)) return it.publishStatus;
  const st = it.status;
  if (st === "published") return "available";
  if (st === "hidden" || st === "archived") return "draft";
  if (st === "scheduled") return "draft";
  return "available";
};

const scheduleState = (it = {}, nowIso) => {
  const coming = it.comingAt || "";
  let avail = it.availableAt || "";
  if (!avail && it.status === "scheduled") avail = it.publishAt || "";
  let s = "draft";
  if (coming && nowIso >= coming) s = "coming";
  if (avail && nowIso >= avail) s = "available";
  return s;
};

// Effective state. Public API already annotates each item with `_state`; trust it when present.
// `linkedEndIso` (optional) lets Admin preview reflect the auto "past" transition of linked content.
export const effectiveState = (it = {}, linkedEndIso = "") => {
  if (it._state && RANK[it._state] !== undefined) return it._state;
  const now = new Date().toISOString();
  const b = baseState(it);
  if (b === "past") return "past";
  const s = scheduleState(it, now);
  let eff = RANK[b] >= RANK[s] ? b : s;
  if (eff === "available") {
    const pastAt = it.pastAt || "";
    if ((pastAt && now >= pastAt) || (linkedEndIso && now >= linkedEndIso)) eff = "past";
  }
  return eff;
};

export const isVisible = (it) => effectiveState(it) !== "draft";
export const isLocked = (it) => effectiveState(it) === "coming"; // visible but main action locked
export const isAvailable = (it) => effectiveState(it) === "available";
export const isPast = (it) => effectiveState(it) === "past"; // visible archive; action closed

// ---- Statut temporel (À venir / En cours / Terminé / Complet) — annoté `_temporal` par le backend ----
export const TEMPORAL_LABELS = {
  a_venir: { label: "À venir", tone: "text-sky-300 border-sky-400/40" },
  en_cours: { label: "En cours", tone: "text-green-300 border-green-400/40" },
  termine: { label: "Terminé", tone: "text-white/50 border-white/15" },
  complet: { label: "Complet", tone: "text-red-300 border-red-400/40" },
};
export const temporalBadge = (it = {}) => {
  const t = it._temporal;
  return t && TEMPORAL_LABELS[t] ? TEMPORAL_LABELS[t] : null;
};

// ---- Durée d'une formation (sessions) : « 3 h », « 2 jours », « 1 journée » ----
const parseDate = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const parseTime = (s) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(s || ""));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
export const formationDurationLabel = (it = {}) => {
  if (it.duration) return it.duration; // texte libre renseigné = prioritaire
  const sessions = it.sessions || [];
  if (!sessions.length) return "";
  const parts = [];
  for (const s of sessions) {
    const sd = parseDate(s.date);
    if (!sd) continue;
    const ed = parseDate(s.endDate) || sd;
    const days = Math.round((ed - sd) / 86400000) + 1;
    if (days > 1) { parts.push(`${days} jours`); continue; }
    if (!s.allDay) {
      const t1 = parseTime(s.time), t2 = parseTime(s.endTime);
      if (t1 != null && t2 != null && t2 > t1) {
        const mins = t2 - t1;
        parts.push(mins % 60 === 0 ? `${mins / 60} h` : `${Math.floor(mins / 60)} h ${mins % 60} min`);
        continue;
      }
    }
    parts.push("1 journée");
  }
  return parts[0] || "";
};

// Badge label for a past item, adapted to the type of content it relates to.
export const pastBadgeLabel = (it = {}) => {
  switch (it.linkedType) {
    case "formation": return "FORMATION TERMINÉE";
    case "event": return "ÉVÉNEMENT PASSÉ";
    case "festival": return "ÉDITION PASSÉE";
    default: return "ARCHIVE";
  }
};
